import { useState, useRef, useEffect } from 'react';

/**
 * InlineQuantityInput - Compact inline editable quantity input with kg suffix
 *
 * Features:
 * - Display mode shows value with "kg" suffix (e.g., "1.5kg")
 * - Click/focus switches to edit mode with editable input
 * - Enter key triggers onBlur (save)
 * - Escape key reverts to original value
 * - Validates numeric input (positive numbers, decimals allowed)
 * - Compact sizing suitable for table cells
 *
 * @param {number} value - Current quantity value
 * @param {function} onChange - Called when value changes during editing
 * @param {function} onBlur - Called when input loses focus or Enter is pressed
 * @param {boolean} disabled - Whether the input is disabled
 * @param {number} min - Minimum allowed value (default 0, set to 0 for adjustments that allow zeroing out)
 * @param {string} className - Additional CSS classes
 */
export default function InlineQuantityInput({
  value,
  onChange,
  onBlur,
  disabled = false,
  min = 0,
  className = ''
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value?.toString() || '');
  const [originalValue, setOriginalValue] = useState(value);
  const inputRef = useRef(null);

  // Update edit value when prop value changes externally
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value?.toString() || '');
      setOriginalValue(value);
    }
  }, [value, isEditing]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = () => {
    if (!disabled) {
      setIsEditing(true);
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;

    // Allow empty string for clearing
    if (newValue === '') {
      setEditValue('');
      return;
    }

    // Allow partial decimal inputs like "1." or ".5"
    const isPartialDecimal = /^(\d*\.?\d*)$/.test(newValue);

    if (isPartialDecimal) {
      setEditValue(newValue);

      // Only call onChange if it's a valid complete number
      const numValue = parseFloat(newValue);
      if (!isNaN(numValue) && numValue >= min) {
        onChange?.(numValue);
      }
    }
  };

  const handleBlur = () => {
    const numValue = parseFloat(editValue);

    // If invalid or negative, revert to original
    if (isNaN(numValue) || numValue < min) {
      setEditValue(originalValue?.toString() || '');
      setIsEditing(false);
      return;
    }

    setIsEditing(false);
    onBlur?.(numValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBlur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditValue(originalValue?.toString() || '');
      setIsEditing(false);
    }
  };

  const displayValue = value !== null && value !== undefined
    ? `${parseFloat(value).toFixed(1)}kg`
    : '0.0kg';

  if (isEditing) {
    return (
      <div className={`inline-flex items-center gap-1 ${className}`}>
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          value={editValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className="w-16 h-7 min-h-7 px-2 py-1 bg-white rounded-md shadow-sm outline outline-1 outline-neutral-400 text-sm focus:outline-2 focus:outline-primary"
          aria-label="Edit quantity"
        />
        <span className="text-muted-foreground text-sm">
          kg
        </span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={`inline-flex items-center px-2 py-1 rounded-md hover:bg-muted/50 transition-colors ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      } ${className}`}
      aria-label="Edit quantity"
    >
      <span className="text-sm">
        {displayValue}
      </span>
    </button>
  );
}
